import React, { useEffect, useState } from 'react';
import { Carousel, Row, Col } from 'react-bootstrap';
import useReRouteHook from '../../../assets/hooks/useReRouteHook';
import VideoModal from './VideoModal';
import './VideoSlider.css';
import { toAbsoluteUrl } from '../../../assets/helpers/AssetHelpers';
import { Link, useLocation, useNavigate } from 'react-router-dom';
interface VideoData {
	id: string;
	title: string;
	category: string;
	element_note: string;
	parent: string;
	faq_category_hierarchy: {
		3: string;
		2: string;
		1: string;
	};
	video_faq_id: string;
}

interface VideoSliderProps {
	videoData: VideoData[];
	buttonUrl: string;
	onClick?: () => void;
	isExpanded?: boolean;
	categoryId?: string; // Add this
}

const VideoSlider: React.FC<VideoSliderProps> = ({ videoData, buttonUrl, categoryId }) => {
	const { reRouteButtonRenderer } = useReRouteHook();
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [currentVideoDetails, setCurrentVideoDetails] = useState<{
		video: string | null;
		title: string | null;
		category: string | null;
		categoryUrl: string | null;
	} | null>(null);
	const [windowWidth, setWindowWidth] = useState(window.innerWidth);
	const [activeIndex, setActiveIndex] = useState(0);
	const location = useLocation();
	const isHomePage = location.pathname === '/';
	const isCategoryPage = location.pathname.startsWith('/videos/');
	const [isExpanded, setIsExpanded] = useState(isCategoryPage);
	const isSmallScreen = windowWidth < 576; // Check if the screen is smaller than tablet size
	const navigate = useNavigate();

	// const togglePlay = (videoSrc: string, title: string, category: string) => {
	// 	setCurrentVideoDetails({ video: videoSrc, title, category });
	// 	setIsModalOpen(true);
	// };
	const togglePlay = (videoSrc: string, title: string, category: string, categoryUrl: string) => {
		setCurrentVideoDetails({ video: videoSrc, title, category, categoryUrl });
		setIsModalOpen(true);

		const params = new URLSearchParams();
		params.set('video', videoSrc);
		params.set('title', encodeURIComponent(title));
		params.set('category', encodeURIComponent(category));
		params.set('url', encodeURIComponent(categoryUrl)); // Add URL param

		navigate(`${location.pathname}?${params.toString()}`, { replace: false });
	};

	const handleModalClose = () => {
		setIsModalOpen(false);
		setCurrentVideoDetails(null);

		navigate(location.pathname, { replace: true });
	};

	useEffect(() => {
		const handleResize = () => setWindowWidth(window.innerWidth);
		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, []);

	useEffect(() => {
		const params = new URLSearchParams(location.search);
		const videoParam = params.get('video');
		const titleParam = params.get('title');
		const categoryParam = params.get('category');
		const urlParam = params.get('url');

		if (videoParam && titleParam && categoryParam) {
			const decodedCategory = decodeURIComponent(categoryParam);
			const decodedUrl = urlParam ? decodeURIComponent(urlParam) : '#';

			if (!categoryId || decodedCategory === categoryId) {
				setCurrentVideoDetails({
					video: videoParam,
					title: decodeURIComponent(titleParam),
					category: decodedCategory || categoryId || 'Uncategorized',
					categoryUrl: decodedUrl,
				});
				setIsModalOpen(true);
			}
		}
	}, [location.search, categoryId]);

	const getVisibleVideos = () => {
		if (isExpanded || isCategoryPage) return videoData.length; // Show all videos on category pages
		if (windowWidth >= 992) return 3;
		if (windowWidth >= 768) return 2;
		if (isHomePage) return 3;
		return 1;
	};

	const visibleVideos = getVisibleVideos();

	const shouldRenderButton = () => {
		if (isCategoryPage) return false; // Hide the button on category pages
		if (windowWidth >= 1200) return videoData.length > 3;
		if (windowWidth >= 577) return videoData.length > 2;
		return videoData.length > 1;
	};

	const handleExpandClick = () => {
		setActiveIndex(0);
		setIsExpanded(!isExpanded);
	};

	return (
		<div>
			<div className="slider-container">
				<div className="mx-md-5 mt-md-3 mt-4">
					<Carousel
						interval={null}
						indicators={false}
						// controls={!isExpanded && videoData.length > visibleVideos}
						controls={
							!((isHomePage && isSmallScreen) || isExpanded || videoData.length <= visibleVideos)
						}
						touch={!isHomePage || !isSmallScreen}
						prevIcon={
							<img
								src={toAbsoluteUrl('video-section/arrow-left.svg')}
								alt="Previous"
								className="custom-prev-icon"
							/>
						}
						nextIcon={
							<img
								src={toAbsoluteUrl('video-section/arrow-right.svg')}
								alt="Next"
								className="custom-next-icon"
							/>
						}
						activeIndex={activeIndex} // Control the active slide
						onSelect={(selectedIndex) => setActiveIndex(selectedIndex)} // Update index when slide changes
					>
						{Array.from({ length: Math.ceil(videoData.length / visibleVideos) }, (_, index) => {
							const startIndex = index * visibleVideos;
							const videoChunk = videoData.slice(startIndex, startIndex + visibleVideos);

							return (
								<Carousel.Item key={index} className="px-2">
									<Row>
										{videoChunk.map((item, subIndex) => {
											const iframeMatch = item.element_note?.match(/src="([^"]+)"/);
											const videoSrc = iframeMatch ? iframeMatch[1] : null;
											const categoryUrl = `/category/${item?.faq_category_hierarchy?.[3]}/${item?.faq_category_hierarchy?.[2]}/${item?.faq_category_hierarchy?.[1]}/${item.video_faq_id}`;

											return (
												<Col key={subIndex} lg={4} md={6} sm={12} className="mb-3">
													{videoSrc ? (
														<div
															onClick={() =>
																togglePlay(
																	videoSrc,
																	item.title,
																	item.parent || categoryId || 'Uncategorized',
																	categoryUrl,
																)
															}
															role="button"
															aria-label="Open video modal"
															className="video-section-iframe-container position-relative"
															style={{ width: '100%', height: 'auto' }}>
															<img
																src={`https://img.youtube.com/vi/${item.id}/maxresdefault.jpg`}
																alt={item.title || 'Video thumbnail'}
																className="rounded-4 responsive-iframe video-height-mobile"
																style={{
																	width: '100%',
																	display: 'block',
																	filter: 'brightness(80%)',
																}}
															/>
															<img
																src={toAbsoluteUrl('video-section/play-btn.svg')}
																alt="Play Button"
																className="custom-play-button position-absolute start-50 translate-middle"
																role="button"
																aria-label="Play"
															/>
														</div>
													) : (
														<p>Video source not found</p>
													)}
													<Link
														to={`/category/${item?.faq_category_hierarchy?.[3]}/${item?.faq_category_hierarchy?.[2]}/${item?.faq_category_hierarchy?.[1]}/${item.video_faq_id}`}
														className={`ur_semi color30 mt-1`}
														onClick={(e) => {
															if (
																!item?.faq_category_hierarchy?.[3] ||
																!item?.faq_category_hierarchy?.[2] ||
																!item?.faq_category_hierarchy?.[1] ||
																!item.video_faq_id
															) {
																e.preventDefault();
																return;
															}

															setTimeout(() => {
																const section = document.getElementById('kt_content_container');
																if (section) {
																	const offset = 88;
																	const sectionPosition =
																		section.getBoundingClientRect().top + window.scrollY - offset;
																	window.scrollTo({ top: sectionPosition, behavior: 'smooth' });
																}
															}, 100);
														}}>
														{item.title}
													</Link>

													{/* <div className="ur_semi color30 mt-1">{item.title}</div> */}
												</Col>
											);
										})}
									</Row>
								</Carousel.Item>
							);
						})}
					</Carousel>

					{(isHomePage || shouldRenderButton()) && (
						<div className="d-flex justify-content-end me-3">
							{reRouteButtonRenderer({
								url: buttonUrl,
								classes: 'color1 ur_semi px-0 view-video-btn-border',
								nameText: isExpanded ? 'View Less' : 'View All Videos',
								onClick: handleExpandClick,
							})}
						</div>
					)}
				</div>
			</div>

			<VideoModal
				videoSrc={currentVideoDetails?.video || null}
				isOpen={isModalOpen}
				onClose={handleModalClose}
				title={currentVideoDetails?.title || 'Default Title'}
				category={currentVideoDetails?.category || 'Uncategorized'}
				categoryUrl={currentVideoDetails?.categoryUrl || '#'}
			/>
		</div>
	);
};

export default VideoSlider;
